import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLeads, type CrmLeadFilters } from '@/hooks/useCRM'
import { type CrmLead } from '@/lib/supabase'
import { Search, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

const STAGE_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  replied: 'Replied',
  meeting: 'Meeting',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
}

type LeadTableProps = {
  onSelectLead: (lead: CrmLead) => void
  filters?: CrmLeadFilters
}

export function LeadTable({ onSelectLead, filters }: LeadTableProps) {
  const [search, setSearch] = useState(filters?.search ?? '')
  const [stageFilter, setStageFilter] = useState<CrmLead['pipeline_stage'] | 'all'>(
    filters?.pipeline_stage ?? 'all'
  )
  const [sourceFilter, setSourceFilter] = useState<CrmLead['source'] | 'all'>(
    filters?.source ?? 'all'
  )
  const { leads, loading, error, refetch } = useLeads({
    ...filters,
    search: search || undefined,
    pipeline_stage: stageFilter,
    source: sourceFilter,
  })

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, company, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as CrmLead['pipeline_stage'] | 'all')}
        >
          <option value="all">All stages</option>
          {Object.entries(STAGE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as CrmLead['source'] | 'all')}
        >
          <option value="all">All sources</option>
          <option value="manual">Manual</option>
          <option value="apollo">Apollo</option>
          <option value="inbound">Inbound</option>
          <option value="website">Website</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Leads ({leads.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2 text-left font-medium">Name</th>
                    <th className="px-4 py-2 text-left font-medium">Company</th>
                    <th className="px-4 py-2 text-left font-medium">Email</th>
                    <th className="px-4 py-2 text-left font-medium">Stage</th>
                    <th className="px-4 py-2 text-left font-medium">Source</th>
                    <th className="px-4 py-2 text-left font-medium">Last contact</th>
                    <th className="px-4 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="border-b transition-colors hover:bg-muted/30 cursor-pointer"
                      onClick={() => onSelectLead(lead)}
                    >
                      <td className="px-4 py-2 font-medium">
                        {lead.full_name ||
                          `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() ||
                          '—'}
                      </td>
                      <td className="px-4 py-2">{lead.company ?? '—'}</td>
                      <td className="px-4 py-2">{lead.email ?? '—'}</td>
                      <td className="px-4 py-2">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-xs',
                            lead.pipeline_stage === 'won' && 'bg-green-500/10 text-green-600',
                            lead.pipeline_stage === 'lost' && 'bg-red-500/10 text-red-600',
                            lead.pipeline_stage === 'new' && 'bg-muted text-muted-foreground',
                            !['won', 'lost', 'new'].includes(lead.pipeline_stage) &&
                              'bg-primary/10 text-primary'
                          )}
                        >
                          {STAGE_LABELS[lead.pipeline_stage] ?? lead.pipeline_stage}
                        </span>
                      </td>
                      <td className="px-4 py-2">{lead.source ?? '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {formatDate(lead.last_contacted_at)}
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation()
                            onSelectLead(lead)
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  No leads match your filters.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
